import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Vehicle, ReservationFormData, PaymentBreakdown } from '../types';
import { formatDateTimeDisplay, formatPrice } from '../utils/dateUtils';
import { useBookingStore } from '../stores/bookingStore';
import { productOptionApi, ProductOption } from '../api/productOptionApi';
import { localizeLocation, localizeOption } from '../utils/localization';

interface SelectedCoupon {
  user_coupon_id: number;
  coupon_id: number;
  name: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  discount_amount: number;
}

interface LocationState {
  reservationData: ReservationFormData;
  vehicle: Vehicle;
  startDateTime: Date;
  endDateTime: Date;
  totalAmount: number;
  reservation_number?: string;
  paymentBreakdown?: PaymentBreakdown;
  selectedCoupon?: SelectedCoupon | null;
}

const SuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { reset } = useBookingStore();
  const getLocationDisplay = (value?: string) => localizeLocation(value, i18n.language);
  const getOptionDisplay = (value?: string) => localizeOption(value, i18n.language);

  const state = location.state as LocationState;

  const [selectedInsurance, setSelectedInsurance] = useState<ProductOption | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<ProductOption[]>([]);

  // Debug: 콘솔에 paymentBreakdown 출력
  React.useEffect(() => {
    if (state?.paymentBreakdown) {
      console.log('PaymentBreakdown:', state.paymentBreakdown);
      console.log('SelectedCoupon:', state.selectedCoupon);
    }
  }, [state]);

  React.useEffect(() => {
    if (!state || !state.reservationData || !state.vehicle) {
      navigate('/');
    }
  }, [state, navigate]);

  // 선택된 옵션 정보 가져오기
  useEffect(() => {
    const fetchOptions = async () => {
      if (!state?.reservationData) return;

      try {
        // 전체 활성 옵션 목록을 한 번에 가져오기 (개별 호출 대신 최적화)
        const [insuranceOptions, equipmentOptions] = await Promise.all([
          productOptionApi.getAllProductOptions({ is_active: true, type: 'insurance' }),
          productOptionApi.getAllProductOptions({ is_active: true, type: 'equipment' }),
        ]);

        // 보험 정보 필터링
        if (state.reservationData.selectedInsuranceId) {
          const insurance = insuranceOptions.find(opt => opt.id === state.reservationData.selectedInsuranceId);
          if (insurance) {
            setSelectedInsurance(insurance);
          }
        }

        // 장비 정보 필터링
        if (state.reservationData.selectedEquipmentIds && state.reservationData.selectedEquipmentIds.length > 0) {
          const equipment = equipmentOptions.filter(opt =>
            state.reservationData.selectedEquipmentIds!.includes(opt.id)
          );
          setSelectedEquipment(equipment);
        }
      } catch (error) {
        console.error('Failed to fetch option details:', error);
      }
    };

    fetchOptions();
  }, [state]);

  if (!state || !state.reservationData || !state.vehicle) {
    return null;
  }

  const { reservationData, vehicle, startDateTime, endDateTime, totalAmount, reservation_number, paymentBreakdown, selectedCoupon } = state;

  const handleNewReservation = () => {
    reset();
    navigate('/');
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          {/* Success Message */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-6 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-6">{t('success.title')}</h1>

            <div className="bg-primary-50 rounded-lg p-4 inline-block">
              <p className="text-sm text-gray-600 mb-1">{t('success.reservationNumber')}</p>
              <p className="text-2xl font-bold text-primary-600">{reservation_number || t('success.processing')}</p>
            </div>
          </div>

          {/* Reservation Details */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-6">
            <h2 className="text-2xl font-bold mb-6">{t('success.details')}</h2>

            {/* Vehicle Info */}
            <div className="mb-6 pb-6 border-b">
              <h3 className="font-semibold text-gray-700 mb-3">{t('success.vehicleInfo')}</h3>
              <div className="flex gap-4">
                <img
                  src={`/${vehicle.id}/01.jpg`}
                  alt={vehicle.model_name}
                  className="w-32 h-24 object-contain rounded-lg bg-gray-50"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = vehicle.image_url || '/placeholder-car.svg';
                  }}
                />
                <div className="flex-1">
                  <p className="font-bold text-lg mb-1">{vehicle.model_name}</p>
                  <p className="text-gray-600 text-sm mb-2">{vehicle.car_class || vehicle.vehicle_type}</p>
                  <div className="flex flex-wrap gap-3 text-sm">
                    <span className="flex items-center text-gray-600">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      {vehicle.seating_capacity || vehicle.capacity}{t('success.seats')}
                    </span>
                    <span className="flex items-center text-gray-600">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      {vehicle.fuel_type || vehicle.fuel || t('success.gasoline')}
                    </span>
                    {vehicle.license_plate && (
                      <span className="flex items-center text-gray-600">
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {vehicle.license_plate}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Rental Period */}
            <div className="mb-6 pb-6 border-b">
              <h3 className="font-semibold text-gray-700 mb-3">{t('success.rentalPeriod')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('success.rentalStart')}:</span>
                  <span className="font-medium">{formatDateTimeDisplay(new Date(startDateTime))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('success.returnDue')}:</span>
                  <span className="font-medium">{formatDateTimeDisplay(new Date(endDateTime))}</span>
                </div>
              </div>
            </div>

            {/* Customer Info */}
            <div className="mb-6 pb-6 border-b">
              <h3 className="font-semibold text-gray-700 mb-3">{t('success.customerInfo')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('success.name')}:</span>
                  <span className="font-medium">{reservationData.name}</span>
                </div>
                {reservationData.birthDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('success.birthDate')}:</span>
                    <span className="font-medium">{reservationData.birthDate}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('success.email')}:</span>
                  <span className="font-medium">{reservationData.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('success.phone')}:</span>
                  <span className="font-medium">{reservationData.phone}</span>
                </div>
              </div>
            </div>

            {/* Rental Options */}
            {(reservationData.pickupLocation || selectedInsurance || selectedEquipment.length > 0) && (
              <div className="mb-6 pb-6 border-b">
                <h3 className="font-semibold text-gray-700 mb-3">{t('success.rentalOptions')}</h3>
                <div className="space-y-2">
                  {reservationData.pickupLocation && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('success.pickup')}:</span>
                      <span className="font-medium">{getLocationDisplay(reservationData.pickupLocation)}</span>
                    </div>
                  )}
                  {reservationData.returnLocation && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('success.return')}:</span>
                      <span className="font-medium">{getLocationDisplay(reservationData.returnLocation)}</span>
                    </div>
                  )}
                  {selectedInsurance && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('success.insurance')}:</span>
                      <span className="font-medium">{getOptionDisplay(selectedInsurance.name)}</span>
                    </div>
                  )}
                  {selectedEquipment.length > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('success.equipment')}:</span>
                      <span className="font-medium">{selectedEquipment.map(eq => getOptionDisplay(eq.name)).join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payment Info */}
            <div>
              <h3 className="font-semibold text-gray-700 mb-3">{t('success.paymentInfo')}</h3>
              <div className="space-y-2">
                {paymentBreakdown ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('success.rentalFee')}:</span>
                      <span>{formatPrice(paymentBreakdown.rental_fee)}</span>
                    </div>
                    {paymentBreakdown.insurance_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('success.insuranceFee')}:</span>
                        <span>{formatPrice(paymentBreakdown.insurance_fee)}</span>
                      </div>
                    )}
                    {paymentBreakdown.delivery_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('success.deliveryFee')}:</span>
                        <span>{formatPrice(paymentBreakdown.delivery_fee)}</span>
                      </div>
                    )}
                    {paymentBreakdown.equipment_fee > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('success.equipmentFee')}:</span>
                        <span>{formatPrice(paymentBreakdown.equipment_fee)}</span>
                      </div>
                    )}
                    {selectedCoupon && selectedCoupon.discount_amount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>{t('success.couponDiscount')} ({selectedCoupon.name}):</span>
                        <span>-{formatPrice(selectedCoupon.discount_amount)}</span>
                      </div>
                    )}
                    {!selectedCoupon && paymentBreakdown.discount && paymentBreakdown.discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>{t('success.discount')}:</span>
                        <span>-{formatPrice(paymentBreakdown.discount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-3 border-t font-bold text-lg">
                      <span>{t('success.totalPayment')}:</span>
                      <span className="text-primary-600">
                        {formatPrice(
                          paymentBreakdown.rental_fee +
                          (paymentBreakdown.insurance_fee || 0) +
                          (paymentBreakdown.delivery_fee || 0) +
                          (paymentBreakdown.equipment_fee || 0) -
                          (selectedCoupon?.discount_amount || 0) -
                          (paymentBreakdown.discount || 0)
                        )}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between pt-3 border-t font-bold text-lg">
                    <span>{t('success.totalPayment')}:</span>
                    <span className="text-primary-600">
                      {formatPrice(totalAmount)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            <button
              onClick={handlePrintReceipt}
              className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors font-semibold flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              {t('success.print')}
            </button>

            <button
              onClick={handleNewReservation}
              className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-semibold"
            >
              {t('success.home')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
